import {useMutation} from '@tanstack/react-query';
import {useCallback, useMemo} from 'react';
import {appMutationAdapter} from '../adapter/appMutationAdapter';
import {t} from '../common/getTranslations';
import {requestContext} from '../common/requestContext';
import useTypedDispatch from '../common/useTypedDispatch';
import useTypedSelector from '../common/useTypedSelector';
import {useDialog} from '../dialog/useDialog.hook';
import {ResultStatus} from '../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../logging/logEvent';
import {putTiaraObjectProps} from '../logging/putTiaraObjectProps';
import {showToast} from '../toast/toastHandler';
import {setAppSetting} from '../user/UserSlice';

export function useTasteGenreSelectDialog(isMobile: boolean) {
  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile,
  });
  const dispatch = useTypedDispatch();
  const appConfig = useTypedSelector(state => state.user.appConfig);
  const {mutateAsync: appSetting} = useMutation(
    appMutationAdapter.appSetting()
  );

  const onClickTasteGenre = useCallback(
    async (tastesGenreInput: 'F' | 'M') => {
      try {
        const result = await appSetting({
          input: {
            tastesGenre: tastesGenreInput,
          },
        });
        if (result?.appSetting.status !== ResultStatus.Success) {
          throw new Error();
        }
        dispatch(setAppSetting({tastesGenre: tastesGenreInput}));
        if (requestContext.requestInfo.appConfig) {
          requestContext.requestInfo.appConfig.tastesGenre = tastesGenreInput;
        }
      } catch {
        showToast({
          message: t('more_settings_contents_recommendation_genre_error_popup'),
          isMobile,
        });
      }
      hideDialog();
    },
    [appSetting, dispatch, hideDialog, isMobile]
  );

  const dialogElem = useMemo(() => {
    return (
      <DialogWrapper
        {...putTiaraObjectProps({
          click: {layer1: '콘텐츠추천정보설정'},
          customProps: {
            page_keyword:
              appConfig?.appSetting?.tastesGenre === 'F'
                ? '로맨스/로판'
                : '판타지/현판',
          },
        })}
      >
        <Dialog.Dimmed />
        <Dialog.List>
          <Dialog.Label>
            {t('more_settings_contents_recommendation_genre_detail')}
          </Dialog.Label>
        </Dialog.List>
        <Dialog.List className="space-y-0">
          <Dialog.Option
            isChecked={appConfig?.appSetting?.tastesGenre === 'F'}
            onClick={e =>
              LOG_CLICK_EVENT('선호장르설정_클릭', e, () =>
                onClickTasteGenre('F')
              )
            }
            {...putTiaraObjectProps({
              click: {copy: '로맨스/로판'},
            })}
          >
            {t('more_settings_contents_recommendation_genre_romance_popup')}
          </Dialog.Option>
          <Dialog.Option
            isChecked={appConfig?.appSetting?.tastesGenre !== 'F'}
            onClick={e =>
              LOG_CLICK_EVENT('선호장르설정_클릭', e, () =>
                onClickTasteGenre('M')
              )
            }
            {...putTiaraObjectProps({
              click: {copy: '판타지/현판'},
            })}
          >
            {t('more_settings_contents_recommendation_genre_fantasy_popup')}
          </Dialog.Option>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button
            onClick={e =>
              LOG_CLICK_EVENT('선호장르설정_취소_클릭', e, hideDialog)
            }
          >
            {t('common_cancel_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    );
  }, [
    Dialog,
    DialogWrapper,
    appConfig?.appSetting?.tastesGenre,
    hideDialog,
    onClickTasteGenre,
  ]);

  return {
    showDialog,
    tasteGenreSelectDialog: dialogElem,
    tastesGenre: appConfig?.appSetting?.tastesGenre ?? 'M',
  };
}
